import { createEffect, createSignal, Match, onCleanup, Show, Switch, useContext } from 'solid-js';
import clsx from 'clsx';
import { createSlider } from 'solid-slider';
import { SliderProps } from './slider.d';
import theme from '../../style/theme';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { convertRemToPixel } from '../../tools/html-rem-tools';
import { ErrorCatcher } from '../../tools/error-catcher';
import { NavControls } from '../nav-controls/nav-controls';
import { lastPageIndex, nextPageIndex, previousPageIndex } from './slider-paging';

export const Slider = (props: SliderProps) => {
    const AppState = useContext(AppContext);
    const [ isReady, setIsReady ] = createSignal(false);
    const [slides, setSlides] = createSignal([]);

    const DEFAULT_DESKTOP_SPACING_PX = 24;
    const DEFAULT_MOBILE_SPACING_PX = 12;

    /* Matches what keen's own next()/prev() animate at, so paging keeps the old feel. */
    const SLIDE_DURATION_MS = 500;

    /* The WordPress block stores cardsPerView as a string ("1" | "2" | "3"), and it
       reaches us untouched on large desktop. Comparing and subtracting coerces, but
       adding would concatenate — so read it through here, never off props directly. */
    const perView = () => Number(props.slidesPerView) || 1;

    /* No `duration` here: keen has no top-level duration option — it reads
       `defaultAnimation`, and falls back to 500ms, which SLIDE_DURATION_MS matches. */
    const options = {
        loop: false,
        initial: props.initial ? props.initial : 0,

        slides: {
            perView: perView(),
            spacing: props.spacing ? convertRemToPixel(props.spacing) : DEFAULT_DESKTOP_SPACING_PX,
        },
        created: (slider: any) => {
            setSlides(slider.slides);
            setTimeout(() => {
                setIsReady(true);
            }, 150);
        },
    };
    const [sliderDesktop, { current, moveTo, update: updateDesktop }] = createSlider(options);
    const [sliderMobile, { current: sliderMobileCurrent, next: sliderMobileNext, prev: sliderMobilePrev, update: updateMobile }] = createSlider({
        ...options,
        slides: {
            perView: 1,
            spacing: props.spacing ? convertRemToPixel(props.spacing) : DEFAULT_MOBILE_SPACING_PX,
        },
    });

    const [desktopEl, setDesktopEl] = createSignal<HTMLElement>();
    const [mobileEl, setMobileEl] = createSignal<HTMLElement>();

    /* KeenSlider measures the track once and writes slide widths inline, so it never
       followed a container that changed after creation. Re-measure on width change.

       In a createEffect, not the ref callback: onCleanup needs a reactive owner, and
       a ref callback (or a setTimeout inside one) is not one. Observer built
       synchronously so the cleanup always covers it; gated on `mounted` and on width
       alone, since update() changes this subtree's height and would otherwise loop. */
    const keepSliderMeasured = (
        element: () => HTMLElement | undefined,
        mount: (el: HTMLElement) => void,
        update: VoidFunction,
    ) => {
        createEffect(() => {
            const el = element();

            if (!el) {
                return;
            }

            let mounted = false;

            const timer = setTimeout(() => {
                mount(el);
                mounted = true;
                update();
            }, 500);

            let observer: ResizeObserver | undefined;

            if (typeof ResizeObserver !== 'undefined') {
                const target = el.parentElement ?? el;
                let lastWidth = Math.round(target.getBoundingClientRect().width);

                observer = new ResizeObserver(() => {
                    const width = Math.round(target.getBoundingClientRect().width);

                    if (!mounted || width === 0 || width === lastWidth) {
                        return;
                    }

                    lastWidth = width;
                    update();
                });

                observer.observe(target);
            }

            onCleanup(() => {
                clearTimeout(timer);
                observer?.disconnect();
            });
        });
    };

    /* Readiness is a data attribute, not a class: keen adds `keen-slider` to these
       elements imperatively, and a reactive class= would rewrite the whole attribute
       and wipe it. Solid writes attributes individually, so they don't collide. */
    keepSliderMeasured(desktopEl, sliderDesktop, updateDesktop);
    keepSliderMeasured(mobileEl, sliderMobile, updateMobile);

    return (
        <ErrorCatcher componentName='Slider'>
            <div
                class={clsx('slider-wrapper', props.isInModal && 'slider-in-modal')}
                style={props.slideHeight ? `--slide-height: ${props.slideHeight}` : ''}
            >
                <Switch>
                    <Match when={AppState.viewport.width > theme.breakpoints.MOBILE}>
                        <div
                            ref={setDesktopEl}
                            data-loading-label={AppState.localize('loading', 'Loading...')}
                            data-slider-ready={isReady() ? '' : undefined}
                            class={clsx(
                                'slider-track flex overflow-clip relative select-none touch-pan-y [-webkit-tap-highlight-color:transparent]',
                                props.allowSlideShadow && 'pb-cvi-5',
                            )}
                        >
                            {props.children}
                        </div>
                    </Match>
                    <Match when={AppState.viewport.width <= theme.breakpoints.MOBILE}>
                        <div
                            ref={setMobileEl}
                            data-loading-label={AppState.localize('loading', 'Loading...')}
                            data-slider-ready={isReady() ? '' : undefined}
                            class={clsx(
                                'slider-track flex overflow-clip relative select-none touch-pan-y [-webkit-tap-highlight-color:transparent]',
                                props.allowSlideShadow && 'pb-cvi-5',
                            )}
                        >
                            {props.children}
                        </div>
                    </Match>
                </Switch>


                {/* Fallback reserves the controls' height, so a slider that cannot page
                    stays as tall as one that can — for mixed rows, and across the
                    breakpoint where perView drops. A single slide can never show
                    controls at any width, so there the reservation is dead space. */}
                <Show
                    when={AppState.viewport.width > theme.breakpoints.MOBILE ? (slides().length - perView() + 1) > 1 : slides().length > 1}
                    fallback={slides().length > 1 ? <div class="h-cvi-13" /> : null}
                >
                    {(() => {
                        /* The mobile track is hard-coded to perView 1, where a page is a
                           single slide — so it stays on keen's own next/prev. */
                        const isMobile = () => AppState.viewport.width <= theme.breakpoints.MOBILE;
                        const activeIndex = () => (isMobile() ? sliderMobileCurrent() : current());
                        const lastIndex = () => (isMobile() ? slides().length - 1 : lastPageIndex(slides().length, perView()));
                        const goPrev = () => (isMobile()
                            ? sliderMobilePrev()
                            : moveTo(previousPageIndex(current(), perView()), SLIDE_DURATION_MS));
                        const goNext = () => (isMobile()
                            ? sliderMobileNext()
                            : moveTo(nextPageIndex(current(), perView(), slides().length), SLIDE_DURATION_MS));

                        return (
                            <NavControls
                                isFirst={activeIndex() === 0}
                                isLast={activeIndex() === lastIndex()}
                                onPrev={goPrev}
                                onNext={goNext}
                            />
                        );
                    })()}
                </Show>
            </div>
        </ErrorCatcher>
    );
};
